/**
 * Formatting Utility Functions
 * Common number, currency, and percentage formatters for the application
 */

/**
 * Format number with thousands separators
 * Example: 1234567.89 → "1,234,568"
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat("en-US").format(Math.round(num));
};

/**
 * Format number in compact notation (K, M, B, T)
 * Example: 1234567890000 → "1.2T"
 */
export const formatCompactNumber = (num: number): string => {
  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (absNum >= 1_000_000_000_000)
    return `${sign}${(absNum / 1_000_000_000_000).toFixed(1)}T`;
  if (absNum >= 1_000_000_000)
    return `${sign}${(absNum / 1_000_000_000).toFixed(1)}B`;
  if (absNum >= 1_000_000) return `${sign}${(absNum / 1_000_000).toFixed(1)}M`;
  if (absNum >= 1_000) return `${sign}${(absNum / 1_000).toFixed(1)}K`;
  return num.toFixed(0);
};

/**
 * Format currency with yen symbol and thousands separators.
 * Returns "N/A" when value is null or undefined (revenue data unavailable).
 * Example: 1234567.89 → "¥1,234,568"
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return "N/A";
  return `¥${formatNumber(value)}`;
};

/**
 * Format currency in compact notation.
 * Returns "N/A" when value is null or undefined (revenue data unavailable).
 * Example: 1234567890000 → "¥1.2T"
 */
export const formatCompactCurrency = (
  value: number | null | undefined,
): string => {
  if (value == null) return "N/A";
  return `¥${formatCompactNumber(value)}`;
};

/**
 * Format percentage value
 * @param value - Number to format (e.g., 0.1234 or 12.34)
 * @param isChange - If true, adds +/- sign and formats as change
 * @param isDecimal - If true, treats value as decimal (0.1234 = 12.34%), otherwise as percentage (12.34 = 12.34%)
 */
export const formatPercentage = (
  value: number,
  isChange = false,
  isDecimal = true,
): string => {
  const percentValue = isDecimal ? value * 100 : value;
  const formatted = percentValue.toFixed(isChange ? 1 : 2);
  return isChange ? `${value >= 0 ? "+" : ""}${formatted}%` : `${formatted}%`;
};

/**
 * Get change type for styling purposes
 */
export const getChangeType = (value: number): "increase" | "decrease" => {
  return value >= 0 ? "increase" : "decrease";
};
